import { useCallback, useEffect, useMemo, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import { createLeadExtractionOrchestrator } from "@/lib/lead/actions/extractionOrchestrator";
import { DeepgramStreamingEngine } from "@/lib/asr/deepgramStreamingEngine";
import { leadMatchesWorkspaceLocation, stampLeadOriginIfBlank } from "@/lib/lead/locationScope";
import { isMeaningful } from "@/lib/lead/utils/isMeaningful";
import { textHeuristics } from "@/lib/lead/extraction/textHeuristics";
import type { LeadDefaultFollowUpPolicy } from "@/lib/lead/extraction/leadDefaultFollowUpPolicy";
import type { MergeLeadResult } from "@/lib/lead/extraction/mergeLeadFromTranscript";
import type { Lead } from "../../types/lead";
import type { Location } from "../../types/location";

type Params = {
  extract: (input: string) => Promise<any>;
  setTranscript: Dispatch<SetStateAction<string>>;
  setLeads: Dispatch<SetStateAction<Lead[]>>;
  setCurrentLead: Dispatch<SetStateAction<Lead | null>>;
  setIsListening: Dispatch<SetStateAction<boolean>>;
  setMergeSuggestion: Dispatch<SetStateAction<{ sourceId: string; reason: string } | null>>;
  currentLeadRef: MutableRefObject<Lead | null>;
  transcriptRef: MutableRefObject<string>;
  listeningRef: MutableRefObject<boolean>;
  asrEngineRef: MutableRefObject<DeepgramStreamingEngine | null>;
  extractionRef: MutableRefObject<ReturnType<typeof createLeadExtractionOrchestrator> | null>;
  extractRequestIdRef: MutableRefObject<number>;
  location: Location | null;
  resetCoachState: () => void;
  clearTranscript: (reason: string) => void;
  leads: Lead[];
  isInitialSyncComplete: boolean;
  emptyLeadAutoDeleteEnabled: boolean;
  purgeLeads: (ids: string[]) => Promise<any>;
  deleteLead: (id: string) => void;
  getLeadDefaultFollowUpPolicy?: () => LeadDefaultFollowUpPolicy;
  /**
   * Phase 2 of transcript merge v2 — opens `<LeadMergeConflictModal>`.
   * When omitted, conflicts are still logged but the modal does not appear.
   */
  onMergeConflicts?: (args: { leadId: string; mergeResult: MergeLeadResult; source: "extract" | "reextract" }) => void;
  handleAsrStatus?: (status: string) => void;
  onAsrTranscriptChunk?: () => void;
};

function hasHighSignalContactCue(text: string): boolean {
  const t = (text || "").toLowerCase();
  if (!t) return false;
  if (/@/.test(t)) return true;
  if (/\b(phone|cell|number|call me at)\b/.test(t)) return true;
  if (/\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/.test(t)) return true;
  return false;
}

export function useLeadWorkspaceExtractionEffects(params: Params) {
  const {
    extract,
    setTranscript,
    setLeads,
    setCurrentLead,
    setIsListening,
    setMergeSuggestion,
    currentLeadRef,
    transcriptRef,
    listeningRef,
    asrEngineRef,
    extractionRef,
    extractRequestIdRef,
    location,
    resetCoachState,
    clearTranscript,
    leads,
    isInitialSyncComplete,
    emptyLeadAutoDeleteEnabled,
    purgeLeads,
    deleteLead,
    getLeadDefaultFollowUpPolicy,
    onMergeConflicts,
    handleAsrStatus,
    onAsrTranscriptChunk,
  } = params;

  const extraction = useMemo(() => {
    const orch = createLeadExtractionOrchestrator(
      {
        extract,
        heuristics: textHeuristics,
        getLeadDefaultFollowUpPolicy,
        getNextRequestId: () => ++extractRequestIdRef.current,
        isLatestRequestId: (id) => id === extractRequestIdRef.current,
        getCurrentLead: () => currentLeadRef.current,
        setCurrentLead,
        setLeads,
        setCurrentLeadRef: (l) => {
          currentLeadRef.current = l;
        },
        onMergeConflicts,
      },
      { debounceMs: 550 }
    );
    extractionRef.current = orch;
    return orch;
  }, [extract, extractRequestIdRef, currentLeadRef, setCurrentLead, setLeads, extractionRef, getLeadDefaultFollowUpPolicy, onMergeConflicts]);

  useEffect(() => () => extraction.cancel(), [extraction]);

  const appendTranscriptChunk = useCallback(
    (chunk: string) => {
      const prev = transcriptRef.current.trim();
      const next = chunk.trim();
      if (!next) return;
      onAsrTranscriptChunk?.();
      const full = prev ? `${prev} ${next}` : next;
      setTranscript(full);
      transcriptRef.current = full;
      const lead = currentLeadRef.current;
      if (!lead) return;
      extractionRef.current?.schedule(lead.id, full);
      // Contact cues should materialize quickly in UI (phone/email) instead of waiting for close-lead flush.
      if (hasHighSignalContactCue(next)) {
        void extractionRef.current?.flush();
      }
    },
    [transcriptRef, setTranscript, currentLeadRef, extractionRef, onAsrTranscriptChunk]
  );

  useEffect(() => {
    asrEngineRef.current = new DeepgramStreamingEngine({
      onAppendText: appendTranscriptChunk,
      onStatus: handleAsrStatus,
      log: (...args) => console.log(...args),
    });
    return () => {
      asrEngineRef.current?.destroy();
    };
  }, [appendTranscriptChunk, asrEngineRef, handleAsrStatus]);

  useEffect(() => {
    if (!location?.id) return;
    setLeads((prev) => {
      let changed = false;
      const next = prev.map((l) => {
        const s = stampLeadOriginIfBlank(l, location.id, location.name);
        if (s !== l) changed = true;
        return s;
      });
      return changed ? next : prev;
    });
    setCurrentLead((cl) => {
      if (!cl) return cl;
      const s = stampLeadOriginIfBlank(cl, location.id, location.name);
      return s !== cl ? s : cl;
    });
  }, [location?.id, location?.name, setLeads, setCurrentLead]);

  useEffect(() => {
    const cl = currentLeadRef.current;
    if (!cl) return;
    const active = (location?.id || "").trim();
    if (!active) return;
    if (!leadMatchesWorkspaceLocation(cl, active)) {
      setCurrentLead(null);
      currentLeadRef.current = null;
      clearTranscript("workspaceLocationScopeChanged");
      extraction.cancel();
      resetCoachState();
      setMergeSuggestion(null);
    }
  }, [location?.id, extraction, currentLeadRef, setCurrentLead, clearTranscript, resetCoachState, setMergeSuggestion]);

  useEffect(() => {
    if (!isInitialSyncComplete || !emptyLeadAutoDeleteEnabled) return;
    const now = Date.now();
    const toDelete = leads.filter(
      (l) =>
        !l.sent &&
        l.name === "New Customer" &&
        !l.transcript?.trim() &&
        !isMeaningful(l.extracted) &&
        now - l.lastUpdated > 60_000
    );
    if (toDelete.length === 0) return;
    const ids = toDelete.map((l) => l.id);
    void purgeLeads(ids);
    ids.forEach((id) => deleteLead(id));
  }, [isInitialSyncComplete, emptyLeadAutoDeleteEnabled, leads, purgeLeads, deleteLead]);

  const stopListeningInternal = useCallback(async () => {
    const eng = asrEngineRef.current;
    if (!eng) return;
    setIsListening(false);
    listeningRef.current = false;
    await eng.stop();
  }, [asrEngineRef, setIsListening, listeningRef]);

  return { extraction, stopListeningInternal };
}
